/**
 * ImagePanel3D - Creates a 3D plane behind the model to display images
 * This allows images to be positioned properly in 3D space
 */
import * as THREE from "three";

export class ImagePanel3D {
    constructor(scene, camera) {
        this.scene = scene;
        this.camera = camera;
        this.plane = null;
        this.texture = null;
        this.material = null;
        this.border = null;
        this.isVisible = false;
        this.padding = 10; // currently unused, but kept if you want pixel-based logic later

        // 🔥 MAIN SIZE + POSITION CONTROLS
        // Height of the image in 3D units (bigger = larger panel)
        this.panelHeight = 8; // try 4.5, 6, 8, etc.
        // Where the panel sits in the world
        this.panelPosition = new THREE.Vector3(-20, 3.5, -2.5);

        // Create the mesh but don't add to scene yet
        this.createPanel();

        // Add click handler to close panel when clicking elsewhere
        this.setupClickHandler();
    }

    /**
     * Create the 3D panel plane
     */
    createPanel() {
        // Create a default empty texture
        this.texture = new THREE.Texture();

        // Create material with transparency
        this.material = new THREE.MeshBasicMaterial({
            map: this.texture,
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide,
        });

        // Start with a 1x1 plane; we'll resize once the real image is loaded
        const geometry = new THREE.PlaneGeometry(1, 1);

        // Create mesh
        this.plane = new THREE.Mesh(geometry, this.material);

        // Initial position (will be overridden in positionPanel)
        this.plane.position.set(0, 1.5, -2);
        this.plane.scale.set(1, 1, 1);
    }

    /**
     * Show an image on the 3D panel
     * @param {string} imageUrl - URL of the image to display
     * @param {string} description - Description text (unused but kept for API compatibility)
     */
    async showImage(imageUrl, description) {
        if (!imageUrl) {
            console.error("No image URL provided");
            return false;
        }

        console.log(`Loading image for 3D panel: ${imageUrl}`);
        console.log("Scene exists:", !!this.scene);
        console.log("Camera exists:", !!this.camera);

        try {
            // Validate scene + camera
            if (!this.scene) {
                console.error("3D scene is not available");
                return false;
            }
            if (!this.camera) {
                console.error("Camera is not available");
                return false;
            }

            // Load the texture
            const texture = await this.loadTexture(imageUrl);
            this.texture = texture;

            if (!texture.image) {
                console.error("Loaded texture has no image data");
                return false;
            }

            // Calculate aspect ratio and adjust plane geometry
            const aspectRatio =
                texture.image.height > 0
                    ? texture.image.width / texture.image.height
                    : 16 / 9;

            console.log("Image loaded with aspect ratio:", aspectRatio);

            // 🔹 Set the plane size (height = panelHeight, width = height * aspect)
            const height = this.panelHeight;
            const width = height * aspectRatio;

            // Update plane geometry to exact world size
            this.plane.geometry.dispose();
            this.plane.geometry = new THREE.PlaneGeometry(width, height);

            // Reset scale to 1 (we encode size in geometry, not scale)
            this.plane.scale.set(1, 1, 1);

            // Update material
            this.material.map = this.texture;
            this.material.needsUpdate = true;
            this.material.opacity = 0; // start from invisible for animation

            // Add / update border
            this.addBorder();

            // Add the plane to the scene if not already there
            if (!this.isVisible) {
                this.scene.add(this.plane);
                this.isVisible = true;
                console.log("Added plane to scene");
            }

            // Position and orient panel
            this.positionPanel();
            console.log(
                "Plane positioned at:",
                this.plane.position.x,
                this.plane.position.y,
                this.plane.position.z,
            );

            // Animate appearance
            this.animatePanel(true);

            console.log("3D image panel created and added to scene");
            return true;
        } catch (error) {
            console.error("Error showing image on 3D panel:", error);
            return false;
        }
    }

    /**
     * Add or update a border around the image
     */
    addBorder() {
        const width = this.plane.geometry.parameters.width;
        const height = this.plane.geometry.parameters.height;

        // Remove old border if exists
        if (this.border) {
            this.scene.remove(this.border);
            this.border.geometry.dispose();
            this.border.material.dispose();
        }

        const borderGeometry = new THREE.EdgesGeometry(
            new THREE.BoxGeometry(width + 0.05, height + 0.05, 0.01),
        );
        const borderMaterial = new THREE.LineBasicMaterial({
            color: 0xb8952d, // Gold
            linewidth: 2,
            transparent: true,
            opacity: 0,
        });

        this.border = new THREE.LineSegments(borderGeometry, borderMaterial);

        // Align with panel
        this.border.position.copy(this.plane.position);
        this.border.quaternion.copy(this.plane.quaternion);
        this.border.position.z += 0.001; // small offset to avoid z-fighting

        this.scene.add(this.border);
    }

    /**
     * Load a texture from URL
     */
    loadTexture(url) {
        return new Promise((resolve) => {
            const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
            console.log("Loading image through proxy:", proxyUrl);

            const loader = new THREE.TextureLoader();
            loader.crossOrigin = "anonymous";

            loader.load(
                proxyUrl,
                (texture) => {
                    console.log("Texture loaded successfully through proxy!");
                    resolve(texture);
                },
                (progress) => {
                    if (progress.lengthComputable) {
                        const percent =
                            (progress.loaded / progress.total) * 100;
                        if (percent % 25 < 1) {
                            console.log(
                                `Texture loading: ${Math.round(percent)}% complete`,
                            );
                        }
                    }
                },
                (error) => {
                    console.error(
                        "Error loading texture through proxy:",
                        error,
                    );

                    // Fallback texture
                    const canvas = document.createElement("canvas");
                    canvas.width = 1024;
                    canvas.height = 1024;
                    const context = canvas.getContext("2d");

                    const gradient = context.createLinearGradient(
                        0,
                        0,
                        canvas.width,
                        canvas.height,
                    );
                    gradient.addColorStop(0, "#6a11cb");
                    gradient.addColorStop(1, "#2575fc");
                    context.fillStyle = gradient;
                    context.fillRect(0, 0, canvas.width, canvas.height);

                    context.font = "24px Arial";
                    context.fillStyle = "white";
                    context.textAlign = "center";
                    context.fillText(
                        "Image could not be loaded",
                        canvas.width / 2,
                        canvas.height / 2,
                    );
                    context.fillText(
                        "Please try again later",
                        canvas.width / 2,
                        canvas.height / 2 + 30,
                    );

                    const fallbackTexture = new THREE.CanvasTexture(canvas);
                    console.log("Created fallback texture");
                    resolve(fallbackTexture);
                },
            );
        });
    }

    /**
     * Position the panel in 3D space relative to the model and camera
     */
    positionPanel() {
        if (!this.camera || !this.plane) return;

        const cameraPosition = this.camera.position.clone();
        console.log("Camera position:", cameraPosition);

        // Use configured world position
        this.plane.position.copy(this.panelPosition);
        this.plane.lookAt(cameraPosition);

        // Keep border aligned
        if (this.border) {
            this.border.position.copy(this.plane.position);
            this.border.quaternion.copy(this.plane.quaternion);
            this.border.position.z -= 0.001; // tiny offset
        }

        console.log("Panel positioned at:", this.panelPosition);

        // Force a render update
        if (window.modelViewer && window.modelViewer.renderer) {
            window.modelViewer.renderer.render(this.scene, this.camera);
            console.log("Forced render update");
        }
    }

    /**
     * Hide the panel
     */
    hidePanel() {
        if (!this.isVisible) return;

        this.animatePanel(false);
    }

    /**
     * Animate the panel appearing or disappearing
     */
    animatePanel(show) {
        const startOpacity = show ? 0 : 1;
        const endOpacity = show ? 1 : 0;
        const duration = 800;
        const startTime = performance.now();

        // Pulse scale on show
        let originalScale = this.plane.scale.clone();
        if (show) {
            const expandedScale = originalScale.clone().multiplyScalar(1.2);
            this.plane.scale.copy(expandedScale);
            if (this.border) this.border.scale.copy(expandedScale);

            const scaleBackDuration = 400;
            const scaleStartTime = performance.now();

            const animateScale = (time) => {
                const elapsed = time - scaleStartTime;
                const progress = Math.min(elapsed / scaleBackDuration, 1);
                const easeOut = 1 - Math.pow(1 - progress, 3);

                const currentScale = expandedScale
                    .clone()
                    .lerp(originalScale, easeOut);

                this.plane.scale.copy(currentScale);
                if (this.border) this.border.scale.copy(currentScale);

                if (progress < 1) {
                    requestAnimationFrame(animateScale);
                }
            };

            requestAnimationFrame(animateScale);
        }

        console.log(`Starting panel animation, show: ${show}`);

        this.material.transparent = true;
        if (this.border && this.border.material) {
            this.border.material.transparent = true;
        }

        if (show) {
            this.material.opacity = 0;
            if (this.border && this.border.material) {
                this.border.material.opacity = 0;
            }
        }

        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeProgress =
                progress < 0.5
                    ? 2 * progress * progress
                    : -1 + (4 - 2 * progress) * progress;
            const opacity =
                startOpacity + (endOpacity - startOpacity) * easeProgress;

            this.material.opacity = opacity;
            if (this.border && this.border.material) {
                this.border.material.opacity = opacity;
            }

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                console.log(
                    `Animation complete, final opacity: ${this.material.opacity}`,
                );
                if (!show) {
                    this.scene.remove(this.plane);
                    if (this.border) {
                        this.scene.remove(this.border);
                    }
                    this.isVisible = false;
                    console.log("Panel removed from scene");
                } else if (window.modelViewer?.renderer) {
                    window.modelViewer.renderer.render(this.scene, this.camera);
                }
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Update the panel position when camera moves
     */
    update() {
        if (this.isVisible) {
            this.positionPanel();
        }
    }

    /**
     * Set up a click handler to close the panel when clicking outside it
     */
    setupClickHandler() {
        const canvas = document.getElementById("viewer");
        if (!canvas) {
            console.warn("Canvas element not found for click handler");
            return;
        }

        canvas.addEventListener("click", (event) => {
            if (!this.isVisible || !this.plane) return;

            const raycaster = new THREE.Raycaster();
            const mouse = new THREE.Vector2();

            const rect = canvas.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, this.camera);

            const intersects = raycaster.intersectObject(this.plane);

            if (intersects.length === 0) {
                console.log("Clicked outside image panel, hiding");
                this.hidePanel();
            }
        });
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.plane) {
            this.scene.remove(this.plane);
            this.plane.geometry.dispose();
            this.material.dispose();
            if (this.texture) this.texture.dispose();
        }

        if (this.border) {
            this.scene.remove(this.border);
            this.border.geometry.dispose();
            this.border.material.dispose();
        }
    }
}
